import React, { useState, useCallback } from "react";
import { Input } from "reactstrap";
import debounce from "lodash/debounce";
import { isMobile } from "../../../utils/deviceInfo";

// Reusable debounced search input used by the admin trivia tabs (Surveys,
// Messaging, Knowledge Checks). The parent owns the network call and
// pagination; this component only debounces typing and surfaces clear/clear
// events via callbacks. Kept visually consistent with the Library tab search
// but scoped to avoid importing a "Library" component elsewhere in the code.
const LocalSearchBar = ({
  onSearch,
  onClearSearch,
  isSearching,
  placeholder,
  mobilePlaceholder,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const desktopPh = placeholder || "Search by name...";
  const mobilePh = mobilePlaceholder || placeholder || "Search by name...";

  const performSearch = (inputValue) => {
    if (inputValue && inputValue.trim()) {
      onSearch(inputValue);
    } else {
      onClearSearch();
    }
  };

  // 800ms matches the Library tab feel.
  const debouncedSearch = useCallback(
    debounce((value) => {
      performSearch(value);
    }, 800),
    []
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClear = () => {
    setSearchQuery("");
    onClearSearch();
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <Input
        type="text"
        placeholder={isMobile ? mobilePh : desktopPh}
        value={searchQuery}
        onChange={handleInputChange}
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: isMobile ? "8px" : "6px",
          fontSize: "14px",
          color: "#1a1a1a",
          padding: isMobile ? "10px 40px 10px 40px" : "8px 40px 8px 36px",
          width: "100%",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          outline: "none",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#3b82f6";
          e.target.style.boxShadow = "0 0 0 3px rgba(59,130,246,0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#e5e7eb";
          e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
        }}
      />

      {/* Search icon / loading spinner */}
      <span
        style={{
          position: "absolute",
          left: isMobile ? "14px" : "12px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        {isSearching ? (
          <div
            className="spinner-border spinner-border-sm"
            role="status"
            style={{
              width: "16px",
              height: "16px",
              borderWidth: "2px",
              color: "#3b82f6",
            }}
          >
            <span className="sr-only">Searching...</span>
          </div>
        ) : (
          <i
            className="flaticon-search"
            style={{
              color: "#9ca3af",
              fontSize: "16px",
            }}
          />
        )}
      </span>

      {/* Clear button */}
      {searchQuery && (
        <span
          onClick={handleClear}
          style={{
            position: "absolute",
            right: isMobile ? "14px" : "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#6b7280",
            fontSize: "18px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          ×
        </span>
      )}
    </div>
  );
};

export default LocalSearchBar;
